"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { CART_STORAGE_KEY, cartItemKey, getCartTotal, type CartItem } from "@/lib/cart";
import { getSupabaseBrowserClient } from "@/lib/supabase";

export function CartView() {
  const supabase = getSupabaseBrowserClient();
  const [items, setItems] = useState<CartItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const total = useMemo(() => getCartTotal(items), [items]);

  useEffect(() => {
    function load() {
      const raw = window.localStorage.getItem(CART_STORAGE_KEY);
      setItems(raw ? JSON.parse(raw) as CartItem[] : []);
    }
    load();
    window.addEventListener("abv-cart-updated", load);
    window.addEventListener("storage", load);
    return () => {
      window.removeEventListener("abv-cart-updated", load);
      window.removeEventListener("storage", load);
    };
  }, []);

  function save(next: CartItem[]) {
    setItems(next);
    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event("abv-cart-updated"));
  }

  async function startCheckout() {
    setError(null);
    setLoading(true);

    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      const response = await fetch("/api/checkout/session", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: "Bearer " + token } : {}) },
        body: JSON.stringify({ items }),
      });
      const payload = await response.json() as { url?: string; error?: string };

      if (!response.ok || !payload.url) {
        setError(payload.error ?? "Unable to start checkout.");
        return;
      }

      window.location.href = payload.url;
    } catch {
      setError("Unable to start checkout.");
    } finally {
      setLoading(false);
    }
  }

  if (items.length === 0) {
    return <div className="mt-10 rounded-lg border border-slate-200 bg-white p-6"><p className="text-[#334155]">Your cart is empty.</p><Link href="/products" className="mt-5 inline-flex rounded-md bg-[#F68A00] px-4 py-2 text-sm font-semibold text-white">View products</Link></div>;
  }

  return (
    <div className="mt-10 rounded-lg border border-slate-200 bg-white p-6">
      <div className="grid gap-5 overflow-x-auto">
        {items.map((item) => (
          <div key={cartItemKey(item)} className="flex min-w-[720px] items-center gap-5 border-b border-slate-100 pb-5 last:border-0 last:pb-0">
            <div className="min-w-0 flex-1">
              <Link href={"/products/" + item.slug} className="text-xl font-semibold text-[#111827] hover:text-[#F68A00]">{item.name}</Link>
              <p className="mt-1 text-sm text-[#64748B]">{item.variantName ? item.variantName + " / " + item.variantSku : item.imageLabel}</p>{item.variantDescription ? <p className="mt-1 text-xs text-[#64748B]">{item.variantDescription}</p> : null}
            </div>
            <p className="w-24 text-right font-semibold text-[#111827]">{"$" + item.price.toFixed(2)}</p>
            <label className="flex w-36 items-center justify-end gap-2 text-sm font-semibold text-[#334155]">
              <span>Qty</span>
              <input type="number" min={1} value={item.quantity} onChange={(event) => save(items.map((nextItem) => cartItemKey(nextItem) === cartItemKey(item) ? { ...nextItem, quantity: Number(event.target.value) || 1 } : nextItem))} className="h-10 w-16 rounded-md border border-[#CBD5E1] px-2 py-2 text-center text-sm" />
            </label>
            <button type="button" onClick={() => save(items.filter((nextItem) => cartItemKey(nextItem) !== cartItemKey(item)))} className="h-10 w-24 rounded-md border border-[#CBD5E1] px-3 py-2 text-sm font-semibold text-[#334155] hover:border-red-300 hover:text-red-700">Remove</button>
          </div>
        ))}
      </div>
      <div className="mt-6 flex flex-wrap justify-between gap-4 border-t border-slate-200 pt-6">
        <div>
          <p className="text-lg font-semibold text-[#1F2937]">Subtotal: {"$" + total.toFixed(2)}</p>
          <p className="mt-1 text-sm text-[#334155]">Flat-rate shipping: $9.95, US and Canada only.</p>
          <div className="mt-2 flex items-center gap-2 text-sm text-[#64748B]">
            <span>Secure checkout</span>
            <span className="font-medium text-[#64748B]">by</span>
            <span className="rounded bg-[#635BFF] px-1.5 py-0.5 text-[11px] font-bold leading-none tracking-normal text-white" aria-label="Stripe">stripe</span>
          </div>
        </div>
        <div className="flex gap-3">
          <Link href="/products" className="inline-flex min-h-10 items-center justify-center rounded-md border border-[#CBD5E1] px-4 py-2 text-center text-sm font-semibold text-[#111827]">Continue shopping</Link>
          <button type="button" onClick={startCheckout} disabled={loading} className="inline-flex min-h-10 items-center justify-center rounded-md bg-[#F68A00] px-4 py-2 text-center text-sm font-semibold text-white disabled:opacity-60">{loading ? "Starting..." : "Checkout"}</button>
        </div>
      </div>
      {error ? <p className="mt-4 rounded-md border border-orange-200 bg-orange-50 p-3 text-sm font-medium text-[#7C2D12]">{error}</p> : null}
    </div>
  );
}
